import React from 'react';
import { type DocumentElement as DocumentElementType } from '@eaco/forms-core';
import {
  useElementValue,
  useIsReadOnly,
} from '../DocumentView/DocumentContext';
import HiddenElementBanner from './HiddenElementBanner';
import ArchivedElementBanner from './ArchivedElementBanner';
import { useDefaultValue } from './useDefaultValue';
import { FieldHeader, FieldHelpText, CalendarIcon } from './FieldParts';
import FieldPopover from './datetime/FieldPopover';
import CalendarPanel from './datetime/CalendarPanel';
import TimeWheelPanel from './datetime/TimeWheelPanel';
import {
  formatTime12,
  parse24,
  to24,
  todayISO,
} from './datetime/dateutils';

interface Props {
  element: DocumentElementType;
}

/** Split "yyyy-mm-ddThh:mm" into its date + time parts. */
const splitDateTime = (s: string): { date: string; time: string } => {
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})/.exec(s || '');
  return { date: m ? m[1] : '', time: m ? m[2] : '' };
};

/** dd / mm / yyyy · h:mm AM — the combined trigger format. */
const formatTrigger = (s: string): string => {
  const { date, time } = splitDateTime(s);
  if (!date) return '';
  const [y, mo, d] = date.split('-');
  return `${d} / ${mo} / ${y} · ${formatTime12(parse24(time))}`;
};

/**
 * Render a yyyy-mm-ddThh:mm string as a human-readable Australian-English
 * date + time (e.g. "5 March 2026, 2:30 pm"). Falls back to the raw
 * string if it doesn't parse, so a legacy value is never swallowed.
 */
const formatDateTime = (iso: string): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(iso);
  if (!m) return iso;
  const d = new Date(
    Number(m[1]),
    Number(m[2]) - 1,
    Number(m[3]),
    Number(m[4]),
    Number(m[5]),
  );
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const DateTimePicker: React.FC<Props> = ({ element }) => {
  const [value, setValue] = useElementValue<string>(element.id);
  const isReadOnly = useIsReadOnly();
  const data = element.data as {
    label?: string;
    subLabel?: string;
    helpText?: string;
    required?: boolean;
    defaultValue?: string;
    hidden?: boolean;
  };
  const displayValue = useDefaultValue(value, setValue, data?.defaultValue);
  const label = data?.label || 'Date & Time';
  const subLabel = data?.subLabel;
  const helpText = data?.helpText;
  const required = data?.required || false;

  // Read-only (e.g. a completed result): render the chosen date + time
  // as static text — no datetime input, no picker.
  if (isReadOnly) {
    return (
      <div className="space-y-2">
        <FieldHeader
          htmlFor={element.id}
          label={label}
          subLabel={subLabel}
          required={required}
        />
        <p className="text-sm">
          {displayValue ? (
            formatDateTime(displayValue)
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </p>
        <FieldHelpText>{helpText}</FieldHelpText>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <FieldHeader
        htmlFor={element.id}
        label={label}
        subLabel={subLabel}
        required={required}
      />
      <FieldPopover
        id={element.id}
        icon={<CalendarIcon />}
        displayText={displayValue ? formatTrigger(displayValue) : ''}
        placeholder="dd / mm / yyyy · -- : --"
      >
        {() => {
          const { date, time } = splitDateTime(displayValue || '');
          return (
            <div className="flex" style={{ maxWidth: 400, margin: '0 auto' }}>
              <div className="min-w-0 flex-1 border-r border-[#eef1f4]">
                <CalendarPanel
                  mode="single"
                  compact
                  selected={date}
                  onSelect={(iso) =>
                    setValue(`${iso}T${time || '09:00'}`)
                  }
                />
              </div>
              <div
                className="flex flex-none flex-col justify-center"
                style={{ width: 118 }}
              >
                <TimeWheelPanel
                  variant="combined"
                  time={parse24(time)}
                  onChange={(t) =>
                    setValue(`${date || todayISO()}T${to24(t)}`)
                  }
                />
              </div>
            </div>
          );
        }}
      </FieldPopover>
      <FieldHelpText>{helpText}</FieldHelpText>
      <HiddenElementBanner hidden={data?.hidden} />
      <ArchivedElementBanner archived={element.archived} />
    </div>
  );
};

export default DateTimePicker;
